import * as THREE from "three";
import { LOOKS, buildEnvironment } from "../look";
import { buildTowerModel } from "../views/towerModel";
import { HQ_PATH, HQ_YAW, lib } from "../views/common";
import { HqMaterials } from "../hqMaterials";
import { paveTile } from "../groundPave";
import { WornSkins } from "./wornSkins";
import { SkinKits } from "./kit/runtime";
import { RoadDecor } from "./kit/roadDecor";
import { KitClock } from "./kit/clock";
import { ROAD_KITS } from "./kits";
import { SOCKET_NAMES } from "./kit/types";
import { SKIN_LOOKS } from "./looks";
import { skinnedName } from "./kit/towerModels";
import type { SkinKind } from "@highmarch/domain";
import { glContexts } from "../../infra/glContexts";

/** What a thumbnail shows: the skin's representative model. */
export interface ThumbTarget { readonly kind: SkinKind; readonly skin: string | null; readonly tower?: string; readonly tier?: number }

/**
 * In-engine renders of skinned models (skins overhaul: "thumbnails become renders of the skinned model"): the shop
 * and Skins-screen pictures (`art/interface/skins/<kind>_<skin>.png` 144² and `@2x` 288², written by
 * tools/art/skins/thumbs.mjs through `__tdSkins.thumb`), and the kit capture sheet (`__tdSkins.kitSheet`): a tower
 * kit on all 12 tower types × 3 tiers next to the stock models. Debug only; the game ships the PNGs.
 *
 * Lit like the turntable (skinPreview.ts): the game's painterly look, its IBL, key + fill + sky. Seen from the game
 * camera's 45° turn, a little steeper than the map (30°) so the attachments on top read. The kit clock is frozen at
 * a fixed moment, so the renders are repeatable.
 *
 * Towers (thumbnails and the kit sheet) are seen from the game camera's own side (`GameSide`): the level camera
 * orbits on the world's −x −z side (cameraCore.ts `place`) and the model is turned by HQ_YAW, so it sees the model's
 * +x +z diagonal — the ring sockets' index 3 (`wall3`, `corner3`, `crest3`: kit/sockets.ts). What faces this picture
 * faces the player in a level. Fortress, troop and road pictures keep the opposite diagonal (the gate and the
 * soldier's face); the key and fill lights turn with the camera, so both sides are lit alike.
 */
export class SkinThumbs {
  static readonly TiltDeg = 24;
  static readonly YawDeg = 45;
  /**
   * The unit vector from a subject toward the camera at `tiltDeg` down and `yawDeg` round. `gameSide`: the level
   * camera's side (cameraCore.ts: world −x −z at yaw 45°), else the opposite diagonal (world +x +z).
   */
  static viewDir(tiltDeg: number, yawDeg: number, gameSide: boolean): THREE.Vector3 {
    const tilt = THREE.MathUtils.degToRad(tiltDeg), yaw = THREE.MathUtils.degToRad(yawDeg), s = gameSide ? -1 : 1;
    return new THREE.Vector3(s * Math.sin(yaw) * Math.cos(tilt), Math.sin(tilt), s * Math.cos(yaw) * Math.cos(tilt));
  }
  /** Key and fill light positions for the +x +z view; the game-side view turns them a half turn with the camera. */
  static readonly KeyAt: readonly [number, number, number] = [-3.2, 5.0, 3.6];
  static readonly FillAt: readonly [number, number, number] = [3.4, 1.6, -3.0];
  /** The model fills this fraction of the frame (the picker crops the picture to a disc). */
  static readonly Fill = 0.8;
  /** The kit clock's moment for every capture: the gem mid-turn, the pennants mid-wave, the glints lit. */
  static readonly CaptureAt = 1.35;
  /** The fortress tier a fortress picture shows (the Skins screen's default). */
  static readonly FortressTier = 2;
  /** The towers of a capture sheet, in the balance's order. */
  static readonly Towers = ["archer", "cannon", "frost", "fire", "lightning", "ballista", "poison", "beacon", "mortar", "barracks", "arcane", "hex"];

  /** One thumbnail: `px` square, transparent. */
  static async render(target: ThumbTarget, px: number): Promise<HTMLCanvasElement> {
    const r = SkinThumbs.renderer(px, px);
    const out = document.createElement("canvas");
    out.width = out.height = px;
    try {
      const root = await SkinThumbs.model(target);
      if (root) SkinThumbs.shoot(r, root, out, 0, 0, px, px, SkinThumbs.Fill, target.kind === "tower");
    } finally { SkinThumbs.free(r); }
    return out;
  }

  /**
   * The capture sheet of a tower kit: a row per tower type, stock t1–t3 then the skin's t1–t3, at `cell` px. With
   * `markers` every socket of the stock models is marked (authoring aid: apex/top white, crest gold, wall red, band
   * blue, corner green). `towers` limits the rows (default: every tower type).
   */
  static async kitSheet(skin: string, cell = 150, markers = false, towers: readonly string[] = SkinThumbs.Towers): Promise<HTMLCanvasElement> {
    const L = lib();
    const head = 30, label = 80, cols = 6;
    const out = document.createElement("canvas");
    out.width = label + cols * cell; out.height = head + towers.length * cell;
    const g = out.getContext("2d")!;
    g.fillStyle = "#3e5f34"; g.fillRect(0, 0, out.width, out.height);
    g.fillStyle = "#fff8e6"; g.font = "bold 14px 'Fredoka Display', sans-serif"; g.textBaseline = "middle";
    ["stock t1", "stock t2", "stock t3", "t1", "t2", "t3"].forEach((t, i) => g.fillText(i < 3 ? t : `${SKIN_LOOKS[skin]?.name ?? skin} ${t}`, label + i * cell + 6, head / 2));
    if (!L) return out;
    const kit = SkinKits.towerKit(skin);
    // the stock models, and the skin's own models where it ships them (big kits: TowerKit.model)
    const names = towers.flatMap((t) => [1, 2, 3].flatMap((n) => [`tower_${t}_t${n}`, ...(kit?.model ? [skinnedName(`tower_${t}_t${n}`, kit.model)] : [])]));
    await L.ensure([...names.filter((n) => L.ships(n)), ...(kit?.asset ? [kit.asset] : [])]);
    const r = SkinThumbs.renderer(cell, cell);
    try {
      for (const [row, t] of towers.entries()) {
        g.fillStyle = "#fff8e6"; g.fillText(t, 6, head + row * cell + cell / 2);
        for (let c = 0; c < cols; c++) {
          const root = await SkinThumbs.model({ kind: "tower", skin: c < 3 ? null : skin, tower: t, tier: (c % 3) + 1 });
          if (root && markers && c < 3) SkinThumbs.mark(root);
          if (root) SkinThumbs.shoot(r, root, out, label + c * cell, head + row * cell, cell, cell, 0.86, true);
        }
      }
    } finally { SkinThumbs.free(r); }
    return out;
  }

  /** Small spheres on every socket of a tower model (kitSheet's `markers`). */
  static readonly MarkerColours: Readonly<Record<string, number>> = { apex: 0xffffff, top: 0xdddddd, crest: 0xffc020, wall: 0xff3030, band: 0x3080ff, corner: 0x30d040 };
  private static mark(storeys: THREE.Object3D): void {
    storeys.updateMatrixWorld(true);
    const clone = storeys;
    const inv = new THREE.Matrix4().copy(storeys.matrixWorld).invert();
    const geo = new THREE.SphereGeometry(0.035, 8, 6);
    for (const name of SOCKET_NAMES) {
      const at = SkinKits.socketAt(clone, name, new THREE.Vector3());
      if (!at) continue;
      const dot = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: SkinThumbs.MarkerColours[name.replace(/\d$/, "")] ?? 0xffffff, depthTest: false }));
      dot.renderOrder = 10;
      dot.position.copy(at.applyMatrix4(inv));
      storeys.add(dot);
    }
  }

  /** The skinned model a thumbnail shows (loaded first), or null without the HQ set. */
  private static async model(t: ThumbTarget): Promise<THREE.Object3D | null> {
    const L = lib();
    if (!L) return null;
    if (t.kind === "tower") {
      const kit = SkinKits.towerKit(t.skin);
      const tower = t.tower ?? kit?.preview?.tower ?? "archer", tier = t.tier ?? kit?.preview?.tier ?? 3;
      const own = kit?.model ? skinnedName(`tower_${tower}_t${tier}`, kit.model) : null;
      await L.ensure([`tower_${tower}_t${tier}`, ...(own && L.ships(own) ? [own] : []), ...(kit?.asset ? [kit.asset] : [])]);
      const m = buildTowerModel(tower, false, tier, kit?.model ?? null);
      WornSkins.dressTower(m.storeys, tower, false, t.skin);
      return m.storeys;
    }
    if (t.kind === "fortress") {
      const model = WornSkins.modelOf(t.skin), tier = t.tier ?? SkinThumbs.FortressTier;
      const name = model && L.ships(`base_t${tier}__${model}`) ? `base_t${tier}__${model}` : `base_t${tier}`;
      await L.ensure([name]);
      const m = buildTowerModel("base", true, tier, model);
      WornSkins.dressTower(m.storeys, "base", true, t.skin);
      return m.storeys;
    }
    if (t.kind === "troop") {
      const name = WornSkins.troopModel(t.skin);
      await L.ensure([name]);
      const b = L.clone(`hq:${name}`);
      b.rotation.y = HQ_YAW;
      WornSkins.dressTroop(b, t.skin);
      return b;
    }
    if (t.kind === "road") return SkinThumbs.road(t.skin);
    return null;
  }

  /** A three-tile run of road (straight, corner, straight) in the road skin's tint, with its dressing. */
  private static async road(skin: string | null): Promise<THREE.Object3D | null> {
    const L = lib()!;
    const kit = skin ? ROAD_KITS[skin] : undefined;
    await L.ensure([HQ_PATH.straight, HQ_PATH.corner, ...(kit?.asset ? [kit.asset] : [])]);
    if (!L.has(`hq:${HQ_PATH.straight}`)) return null;
    const g = new THREE.Group();
    g.userData.roadPicture = true;
    const cells = [{ x: 0, y: 0 }, { x: 0, y: 1 }, { x: 0, y: 2 }];
    for (const c of cells) {
      const tile = L.clone(`hq:${HQ_PATH.straight}`);
      tile.position.set(c.x, 0, c.y);
      tile.rotation.y = HQ_YAW;
      g.add(tile);
    }
    // The tint on the tiles' OWN materials (clones): the shared road-tile set HqMaterials.setRoadTint re-tints is the
    // hub's, whose tiles may not be the ones cloned here — the old thumbnails came out all stock-coloured.
    const tint = kit?.tint ?? (skin ? SKIN_LOOKS[skin]?.road : undefined);
    if (tint) {
      const [r, gg, b] = HqMaterials.RoadTileGain;
      const tinted = new Map<THREE.Material, THREE.Material>();
      g.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh || Array.isArray(m.material)) return;
        const src = m.material as THREE.MeshStandardMaterial;
        if (!src.color) return;
        let t = tinted.get(src);
        if (!t) {
          const c = src.clone(); c.color.setRGB(r, gg, b).multiply(new THREE.Color(...tint)); c.onBeforeCompile = src.onBeforeCompile; c.customProgramCacheKey = src.customProgramCacheKey;
          if (kit?.surface && src.map) paveTile(c, kit.surface);            // the skin's own paving (groundPave.ts) on the cobbles
          tinted.set(src, c); t = c;
        }
        m.material = t;
      });
    }
    RoadDecor.build(g, skin, [...cells, { x: 0, y: 3 }]);
    // RoadDecor stands the props on the terrain (groundAt); here they stand on the tiles' cobbles
    const surface = SkinThumbs.roadSurface(g, cells[1]);
    const m4 = new THREE.Matrix4(), p = new THREE.Vector3(), q = new THREE.Quaternion(), sc = new THREE.Vector3();
    for (const c of g.children) {
      const inst = c as THREE.InstancedMesh;
      if (!inst.isInstancedMesh || c.userData.tdRoadDecor !== true) continue;
      for (let i = 0; i < inst.count; i++) { inst.getMatrixAt(i, m4); m4.decompose(p, q, sc); p.y = surface + (inst.userData.tdLift ?? 0); inst.setMatrixAt(i, m4.compose(p, q, sc)); }
      inst.instanceMatrix.needsUpdate = true;
      inst.computeBoundingSphere();
    }
    return g;
  }

  /** The height of the tiles' top (the cobbles) under a cell's centre, by a ray straight down. */
  private static roadSurface(g: THREE.Group, cell: { x: number; y: number }): number {
    g.updateMatrixWorld(true);
    const ray = new THREE.Raycaster(new THREE.Vector3(cell.x, 5, cell.y), new THREE.Vector3(0, -1, 0));
    const tiles = g.children.filter((c) => c.userData.tdRoadDecor !== true);
    const hit = ray.intersectObjects(tiles, true)[0];
    return hit ? hit.point.y : 0;
  }

  private static renderer(w: number, h: number): { r: THREE.WebGLRenderer; scene: THREE.Scene; cam: THREE.OrthographicCamera; key: THREE.DirectionalLight; fill: THREE.DirectionalLight } {
    const r = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    const close = glContexts.open("skin thumbnails");
    r.domElement.addEventListener("webglcontextlost", close, { once: true });
    r.setPixelRatio(1);
    r.setSize(w, h, false);
    r.setClearAlpha(0);
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.toneMapping = THREE.ACESFilmicToneMapping;
    const look = LOOKS.painterly;
    r.toneMappingExposure = look.exposure;
    const scene = new THREE.Scene();
    scene.environment = buildEnvironment(r, look);
    const key = new THREE.DirectionalLight(look.sun.color, look.sun.intensity);
    key.position.set(...SkinThumbs.KeyAt);
    const fill = new THREE.DirectionalLight(look.fill.color, look.fill.intensity);
    fill.position.set(...SkinThumbs.FillAt);
    scene.add(key, fill, new THREE.HemisphereLight(look.hemi.sky, look.hemi.ground, look.hemi.intensity));
    KitClock.frozenAt = SkinThumbs.CaptureAt;
    KitClock.tick(-1);
    return { r, scene, cam: new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 100), key, fill };
  }

  /** Frames `root` from the game camera's angle (`gameSide`: from its side too) and draws it into `out` at (x, y, w, h). */
  private static shoot(k: ReturnType<typeof SkinThumbs.renderer>, root: THREE.Object3D, out: HTMLCanvasElement, x: number, y: number, w: number, h: number, fill: number, gameSide: boolean): void {
    const { r, scene, cam } = k;
    const turn = gameSide ? -1 : 1;
    k.key.position.set(SkinThumbs.KeyAt[0] * turn, SkinThumbs.KeyAt[1], SkinThumbs.KeyAt[2] * turn);
    k.fill.position.set(SkinThumbs.FillAt[0] * turn, SkinThumbs.FillAt[1], SkinThumbs.FillAt[2] * turn);
    // the road picture's tiles carry their own tinted materials (road()); the shared tiles show stock meanwhile
    const before = HqMaterials.roadTint.clone();
    const road = root.userData.roadPicture === true;
    if (road) HqMaterials.setRoadTint(new THREE.Color(1, 1, 1));
    scene.add(root);
    root.updateMatrixWorld(true);
    KitClock.tick(-2);
    const box = new THREE.Box3().setFromObject(root), c = box.getCenter(new THREE.Vector3());
    const dir = SkinThumbs.viewDir(SkinThumbs.TiltDeg, SkinThumbs.YawDeg, gameSide);
    cam.position.copy(c).addScaledVector(dir, 20);
    cam.lookAt(c);
    cam.updateMatrixWorld(true);
    // the box's extent across and up the view, so the model fills the frame whatever its shape
    const inv = cam.matrixWorldInverse, pts = [0, 1].flatMap((i) => [0, 1].flatMap((j) => [0, 1].map((l) => new THREE.Vector3(i ? box.max.x : box.min.x, j ? box.max.y : box.min.y, l ? box.max.z : box.min.z).applyMatrix4(inv))));
    const hx = Math.max(...pts.map((p) => Math.abs(p.x - 0))), hy = Math.max(...pts.map((p) => Math.abs(p.y - 0)));
    const half = Math.max(hx * (h / w), hy) / fill;
    cam.left = (-half * w) / h; cam.right = (half * w) / h; cam.top = half; cam.bottom = -half;
    cam.updateProjectionMatrix();
    r.render(scene, cam);
    out.getContext("2d")!.drawImage(r.domElement, x, y, w, h);
    scene.remove(root);
    if (road) HqMaterials.setRoadTint(before);
  }

  private static free(k: ReturnType<typeof SkinThumbs.renderer>): void {
    KitClock.frozenAt = null;
    k.scene.environment?.dispose();
    k.r.dispose();
    k.r.forceContextLoss();
  }
}
